import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import type { ErrorResponse } from '@remix-run/router';

import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { AuthProviderState } from '../auth';

import { Configuration, RequestOpts } from './autogenerated/pokApiClient';
import { DefaultApi } from './autogenerated/pokApiClient';
import {
  DocumentationContext,
  DocumentationContextInterface,
} from './contexts/DocumentationContext';
import {
  ClientContext,
  ClientContextInterface,
} from './contexts/ClientContext';
import {
  CompanyContext,
  CompanyContextInterface,
} from './contexts/CompanyContext';
import {
  DictionaryContext,
  DictionaryContextInterface,
} from './contexts/DictionaryContext';
import {
  DictionaryTypeContext,
  DictionaryTypeContextInterface,
} from './contexts/DictionaryTypeContext';
import {
  EmployeeContext,
  EmployeeContextInterface,
} from './contexts/EmployeeContext';
import { TeamContext, TeamContextInterface } from './contexts/TeamContext';
import {
  OneToolTeamContext,
  OneToolTeamContextInterface,
} from './contexts/OneToolTeamContext';
import { LoginContext, LoginContextInterface } from './contexts/LoginContext';
import { RoleContext, RoleContextInterface } from './contexts/RoleContext';
import {
  EmployeeRoleContext,
  EmployeeRoleContextInterface,
} from './contexts/EmployeeRoleContext';
import {
  BudgetContext,
  BudgetContextInterface,
} from './contexts/BudgetContext';
import { BrandContext, BrandContextInterface } from './contexts/BrandContext';
import {
  PurchaserContext,
  PurchaserContextInterface,
} from './contexts/PurchaserContext';
import {
  AttachmentContext,
  AttachmentContextInterface,
} from './contexts/AttachmentContext';
import {
  PositionContext,
  PositionContextInterface,
} from './contexts/PositionContext';
import {
  ProjectContext,
  ProjectContextInterface,
} from './contexts/ProjectContext';
import {
  SymphonyClientContext,
  SymphonyClientContextInterface,
} from './contexts/SymphonyClientContext';
import {
  ProfileContext,
  ProfileContextInterface,
} from './contexts/ProfileContext';
import {
  ProfileCategoryContext,
  ProfileCategoryContextInterface,
} from './contexts/ProfileCategoryContext';
import {
  ApplicationContext,
  ApplicationContextInterface,
} from './contexts/ApplicationContext';
import {
  PurchaseInvoiceContext,
  PurchaseInvoiceContextInterface,
} from './contexts/PurchaseInvoiceContext';
import {
  SalesInvoiceContext,
  SalesInvoicesContextInterface,
} from './contexts/SalesInvoicesContext';
import {
  ProjectApplicationContext,
  ProjectApplicationContextInterface,
} from './contexts/ProjectApplicationContext';
import {
  EstimateByMonthContext,
  EstimateByMonthContextInterface,
} from './contexts/EstimateByMonthContext';
import {
  EstimateItemContext,
  EstimateItemContextInterface,
} from './contexts/EstimateItemContext';
import {
  BankAccountContext,
  BankAccountContextInterface,
} from './contexts/BankAccountContext';
import {
  ExchangeApiContext,
  ExchangeApiContextInterface,
} from './contexts/ExchangeApiContext';
import { OrderContext, OrderContextInterface } from './contexts/OrderContext';
import {
  OrderExecutionContext,
  OrderExecutionContextInterface,
} from './contexts/OrderExecutionContext';
import { EmailContext, EmailContextInterface } from './contexts/EmailContext';
import {
  PdfPrinterContext,
  PdfPrinterContextInterface,
} from './contexts/PdfPrinterContext';
import {
  PackageInvoicesXMLContext,
  PackageInvoicesXMLContextInterface,
} from './contexts/PackageInvoicesXMLContext';
import {
  ReportContext,
  ReportContextInterface,
} from './contexts/ReportContext';
import {
  ApplicationUnblockContext,
  ApplicationUnblockContextInterface,
} from './contexts/ApplicationUnblockContext';
import {
  SymphonyContext,
  SymphonyContextInterface,
} from './contexts/SymphonyContext';
import {
  SavedPdfContext,
  SavedPdfContextInterface,
} from './contexts/SavedPdfContext';
import {
  ProjectExcelImportContext,
  ProjectExcelImportContextInterface,
} from './contexts/ProjectExcelImportContext';
import {
  ProjectExcelConfigContext,
  ProjectExcelConfigContextInterface,
} from './contexts/ProjectExcelConfigContext';
import { MemoContext, MemoContextInterface } from './contexts/MemoContext';
import {
  SpecialInvoicingContext,
  SpecialInvoicingContextInterface,
} from './contexts/SpecialInvoicingContext';
import {
  ExcelConfigHeaderContext,
  ExcelConfigHeaderContextInterface,
} from './contexts/ExcelConfigHeaderContext';
import {
  EmailConfigContext,
  EmailConfigContextInterface,
} from './contexts/EmailConfigContext';
import {
  ExcelConfigPositionContext,
  ExcelConfigPositionContextInterface,
} from './contexts/ExcelConfigPositionContext';
import {
  BlockadeMonthContext,
  BlockadeMonthContextInterface,
} from './contexts/BlockadeMonthContext';
import {
  JobConfigContext,
  JobConfigContextInterface,
} from './contexts/JobConfigContext';
import {
  ParamsContext,
  ParamsContextInterface,
} from './contexts/ParamsContext';
import {
  CalendarContext,
  CalendarContextInterface,
} from './contexts/CalendarContext';
import {
  CurrencyContext,
  CurrencyContextInterface,
} from './contexts/CurrencyContext';
import {
  CountryContext,
  CountryContextInterface,
} from './contexts/CountryContext';
import {
  ContractContext,
  ContractContextInterface,
} from './contexts/ContractContext';
import {
  ContractEstimateItemContext,
  ContractEstimateItemContextInterface,
} from './contexts/ContractEstimateItemContext';
import {
  BlockadeMonthContextExceptionInterface,
  BlockadeMonthExceptionContext,
} from './contexts/BlockadeMonthExceptionContext';
import {
  AuthorizedContext,
  AuthorizedContextInterface,
} from './contexts/AuthorizedContext';
import {
  SymphonyImportErrorsContext,
  SymphonyImportErrorsContextInterface,
} from './contexts/SymphonyImportErrorsContext';
import {
  EmailAddressContext,
  EmailAddressContextInterface,
} from './contexts/EmailAddressContext';
import {
  ContactContext,
  ContactContextInterface,
} from './contexts/ContactContext';
import {
  DownloadDocumentsContext,
  DownloadDocumentsContextInterface,
} from './contexts/DownloadDocumentsContext';
import {
  SalesInvoiceGetByIdContext,
  SalesInvoicesGetByIdContextInterface,
} from './contexts/SalesInvoicesGetByIdContext';
import {
  SalesInvoicePaymentsContext,
  SalesInvoicePaymentsContextInterface,
} from './contexts/SalesInvoicePaymentsContext';
import {
  SalesInvoiceOrderNumberContext,
  SalesInvoiceOrderNumberContextInterface,
} from './contexts/SalesInvoiceOrderNumberContext';
import {
  EodLogsContext,
  EodLogsContextInterface,
} from './contexts/EodLogsContext';
import {
  EodImportLogContext,
  EodImportLogContextInterface,
} from './contexts/EodImportLogContext';
import {
  UmpImportContext,
  UmpImportContextInterface,
} from './contexts/UmpImportContext';
import {
  StageDefinitionContext,
  StageDefinitionContextInterface,
} from './contexts/StageDefinitionContext';
import {
  StageHintContext,
  StageHintContextInterface,
} from './contexts/StageHintContext';
import {
  RecommendedPublisherContext,
  RecommendedPublisherContextInterface,
} from './contexts/RecommendedPublisherContext';
import {
  StepDefinitionContext,
  StepDefinitionContextInterface,
} from './contexts/StepDefinitionContext';
import {
  SurveyContext,
  SurveyContextInterface,
} from './contexts/SurveyContext';
import {
  ClientSurveyContext,
  ClientSurveyContextInterface,
} from './contexts/ClientSurveyContext';
import {
  ClientGroupContext,
  ClientGroupContextInterface,
} from './contexts/ClientGroupContext';
import {
  TooltipContext,
  TooltipContextInterface,
} from './contexts/TooltipContext';

export interface PokCoreContextType {
  attachments: AttachmentContextInterface;
  applications: ApplicationContextInterface;
  docs: DocumentationContextInterface;
  clients: ClientContextInterface;
  companies: CompanyContextInterface;
  dictionaries: DictionaryContextInterface;
  dictionaryTypes: DictionaryTypeContextInterface;
  employees: EmployeeContextInterface;
  employeesRoles: EmployeeRoleContextInterface;
  exchangeApi: ExchangeApiContextInterface;
  teams: TeamContextInterface;
  oneToolTeams: OneToolTeamContextInterface;
  budgets: BudgetContextInterface;
  brands: BrandContextInterface;
  logins: LoginContextInterface;
  purchasers: PurchaserContextInterface;
  profiles: ProfileContextInterface;
  profileCategories: ProfileCategoryContextInterface;
  positions: PositionContextInterface;
  symphonyClients: SymphonyClientContextInterface;
  orders: OrderContextInterface;
  ordersExecution: OrderExecutionContextInterface;
  projects: ProjectContextInterface;
  projectsApplication: ProjectApplicationContextInterface;
  roles: RoleContextInterface;
  purchaseInvoices: PurchaseInvoiceContextInterface;
  salesInvoices: SalesInvoicesContextInterface;
  estimatesByMonth: EstimateByMonthContextInterface;
  estimateItems: EstimateItemContextInterface;
  bankAccounts: BankAccountContextInterface;
  emails: EmailContextInterface;
  pdfPrinters: PdfPrinterContextInterface;
  packagesXML: PackageInvoicesXMLContextInterface;
  reports: ReportContextInterface;
  downloadDoc: DownloadDocumentsContextInterface;
  symphonies: SymphonyContextInterface;
  savedPdfs: SavedPdfContextInterface;
  projectExcelImports: ProjectExcelImportContextInterface;
  projectExcelConfigs: ProjectExcelConfigContextInterface;
  memos: MemoContextInterface;
  excelConfigHeaders: ExcelConfigHeaderContextInterface;
  excelConfigPositions: ExcelConfigPositionContextInterface;
  blockades: BlockadeMonthContextInterface;
  blockadesException: BlockadeMonthContextExceptionInterface;
  getCompanyId: () => string;
  setCompanyId: (unitId: string) => void;
  getLanguage: () => string;
  setLanguage: (lang: string) => void;
  registerConnectionHandler: (ensureConnected: () => Promise<void>) => void;
  applicationsUnblock: ApplicationUnblockContextInterface;
  specialInvoicing: SpecialInvoicingContextInterface;
  emailConfig: EmailConfigContextInterface;
  calendars: CalendarContextInterface;
  jobConfig: JobConfigContextInterface;
  params: ParamsContextInterface;
  currencies: CurrencyContextInterface;
  countries: CountryContextInterface;
  contracts: ContractContextInterface;
  contractsEstimateItems: ContractEstimateItemContextInterface;
  authorized: AuthorizedContextInterface;
  symphonyImportErrors: SymphonyImportErrorsContextInterface;
  emailAddresses: EmailAddressContextInterface;
  contacts: ContactContextInterface;
  salesInvoicesGetById: SalesInvoicesGetByIdContextInterface;
  salesInvoicePayments: SalesInvoicePaymentsContextInterface;
  salesInvoiceOrderNumbers: SalesInvoiceOrderNumberContextInterface;
  eodLogs: EodLogsContextInterface;
  eodImportLogs: EodImportLogContextInterface;
  umpImport: UmpImportContextInterface;
  stageDefinitions: StageDefinitionContextInterface;
  stageHints: StageHintContextInterface;
  stepDefinitions: StepDefinitionContextInterface;
  recommendedPublishers: RecommendedPublisherContextInterface;
  surveys: SurveyContextInterface;
  clientSurveys: ClientSurveyContextInterface;
  clientGroups: ClientGroupContextInterface;
  tooltips: TooltipContextInterface;
}

const getContext: (
  api: DefaultApi,
  getCompanyId: () => string,
  companyId: string | undefined,
  companySetter: (unitId: string) => void,
  getLanguage: () => string,
  language: string | undefined,
  languageSetter: (lang: string) => void,
  registerConnectionHandler: (ensureConnected: () => Promise<void>) => void,
) => PokCoreContextType = (
  api,
  getCompanyId,
  companyId,
  companySetter,
  getLanguage,
  language,
  languageSetter,
  registerConnectionHandler,
) => {
  return {
    attachments: AttachmentContext(api),
    applications: ApplicationContext(api),
    docs: DocumentationContext(api),
    clients: ClientContext(api),
    companies: CompanyContext(api),
    dictionaries: DictionaryContext(api),
    dictionaryTypes: DictionaryTypeContext(api),
    employees: EmployeeContext(api),
    employeesRoles: EmployeeRoleContext(api),
    exchangeApi: ExchangeApiContext(api),
    teams: TeamContext(api),
    oneToolTeams: OneToolTeamContext(api),
    budgets: BudgetContext(api),
    brands: BrandContext(api),
    logins: LoginContext(api),
    orders: OrderContext(api),
    ordersExecution: OrderExecutionContext(api),
    purchasers: PurchaserContext(api),
    profiles: ProfileContext(api),
    profileCategories: ProfileCategoryContext(api),
    positions: PositionContext(api),
    projects: ProjectContext(api),
    projectsApplication: ProjectApplicationContext(api),
    symphonyClients: SymphonyClientContext(api),
    roles: RoleContext(api),
    purchaseInvoices: PurchaseInvoiceContext(api),
    salesInvoices: SalesInvoiceContext(api),
    estimatesByMonth: EstimateByMonthContext(api),
    estimateItems: EstimateItemContext(api),
    bankAccounts: BankAccountContext(api),
    emails: EmailContext(api),
    pdfPrinters: PdfPrinterContext(api),
    packagesXML: PackageInvoicesXMLContext(api),
    reports: ReportContext(api),
    downloadDoc: DownloadDocumentsContext(api),
    symphonies: SymphonyContext(api),
    savedPdfs: SavedPdfContext(api),
    projectExcelImports: ProjectExcelImportContext(api),
    projectExcelConfigs: ProjectExcelConfigContext(api),
    memos: MemoContext(api),
    excelConfigHeaders: ExcelConfigHeaderContext(api),
    excelConfigPositions: ExcelConfigPositionContext(api),
    getCompanyId: getCompanyId,
    companyId,
    setCompanyId: companySetter,
    getLanguage: getLanguage,
    language,
    setLanguage: languageSetter,
    registerConnectionHandler,
    applicationsUnblock: ApplicationUnblockContext(api),
    specialInvoicing: SpecialInvoicingContext(api),
    emailConfig: EmailConfigContext(api),
    jobConfig: JobConfigContext(api),
    params: ParamsContext(api),
    blockades: BlockadeMonthContext(api),
    blockadesException: BlockadeMonthExceptionContext(api),
    calendars: CalendarContext(api),
    currencies: CurrencyContext(api),
    countries: CountryContext(api),
    contracts: ContractContext(api),
    contractsEstimateItems: ContractEstimateItemContext(api),
    authorized: AuthorizedContext(api),
    symphonyImportErrors: SymphonyImportErrorsContext(api),
    emailAddresses: EmailAddressContext(api),
    contacts: ContactContext(api),
    salesInvoicesGetById: SalesInvoiceGetByIdContext(api),
    salesInvoicePayments: SalesInvoicePaymentsContext(api),
    salesInvoiceOrderNumbers: SalesInvoiceOrderNumberContext(api),
    eodLogs: EodLogsContext(api),
    eodImportLogs: EodImportLogContext(api),
    umpImport: UmpImportContext(api),
    stageDefinitions: StageDefinitionContext(api),
    stageHints: StageHintContext(api),
    stepDefinitions: StepDefinitionContext(api),
    recommendedPublishers: RecommendedPublisherContext(api),
    surveys: SurveyContext(api),
    clientSurveys: ClientSurveyContext(api),
    clientGroups: ClientGroupContext(api),
    tooltips: TooltipContext(api),
  };
};

const notReadyApi = new DefaultApi();
const notReadyHandler = () => {
  // to be set later
};
const notReadyHandlerString = () => {
  // to be set later
  return '';
};

export const PokCoreContext = React.createContext(
  getContext(
    notReadyApi,
    notReadyHandlerString,
    undefined,
    notReadyHandler,
    notReadyHandlerString,
    undefined,
    notReadyHandler,
    notReadyHandler,
  ),
);

class APIWithHandler extends DefaultApi {
  constructor(
    configuration: Configuration,
    private responseExceptionHandler: (
      response: ErrorResponse,
    ) => ErrorResponse,
    private ensureConnected: () => Promise<void>,
    private auth: AuthProviderState,
  ) {
    super(configuration);
  }

  protected async request(context: RequestOpts): Promise<Response> {
    await this.ensureConnected(); //TODO czy potrzebne?
    let response = new Response();

    if (!this.auth) {
      throw new Error('No auth');
    }

    try {
      const token = localStorage.getItem('pok-jwt-token');
      context.headers['Authorization'] = 'Bearer ' + token;
      context.headers['pok-company'] =
        JSON.parse(sessionStorage.getItem('pok-company') || '{}')?.id || '';
      context.headers['pok-language'] =
        JSON.parse(sessionStorage.getItem('pok-language') || '""') || '';
      response = await super.request(context);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (errorResponse: any) {
      if (errorResponse.response.status !== 401) {
        throw errorResponse;
      }

      if (context.path === '/login/refreshTokens') {
        const rt = localStorage.getItem('pok-jwt-refresh-token');
        if (rt) {
          this.loginControllerLogout(rt).catch(() => {});
        }
        this.auth.logout();
        throw errorResponse;
      }

      const firstRefreshToken = localStorage.getItem('pok-jwt-refresh-token');
      await this.auth.mutex?.runExclusive(async () => {
        const refreshToken = localStorage.getItem('pok-jwt-refresh-token');
        if (!refreshToken) {
          throw errorResponse;
        }
        try {
          if (refreshToken === firstRefreshToken) {
            const tokens = await this.loginControllerRefreshToken(refreshToken);
            localStorage.setItem('pok-jwt-refresh-token', tokens.refreshToken);
            localStorage.setItem('pok-jwt-token', tokens.accessToken);
          }
          const newToken = localStorage.getItem('pok-jwt-token');

          if (!newToken) {
            throw errorResponse;
          }
          context.headers['Authorization'] = 'Bearer ' + newToken;
          response = await super.request(context);
          return response;
        } catch {
          const rt2 = localStorage.getItem('pok-jwt-refresh-token');
          if (rt2) {
            this.loginControllerLogout(rt2).catch(() => {});
          }
          this.auth.logout();
          throw errorResponse;
        }
      });
    }
    return response;
  }
}

export const PokCoreProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth();
  const notifications = useNotifications();
  const [companyId, setCompanyId] = useState<string>();
  const [language, setLanguage] = useState<string>();
  const [context, setDefaultContext] = useState(
    getContext(
      new APIWithHandler(
        new Configuration({
          basePath: process.env.REACT_APP_POKAPI_URL,
        }),
        (response: ErrorResponse) => {
          if (response.status === 403) {
            notifications.unauthorized();
          }
          throw response;
        },
        async () => {
          if (waitingRoomRef.current) {
            await waitingRoomRef.current();
          }
        },
        auth,
      ),
      () => JSON.parse(sessionStorage.getItem('pok-company') || '{}')?.id || '',
      companyId,
      notReadyHandler,
      () => JSON.parse(sessionStorage.getItem('pok-language') || '""') || '',
      language,
      notReadyHandler,
      notReadyHandler,
    ),
  );
  const waitingRoomRef = useRef<() => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    const configuration = new Configuration({
      basePath: process.env.REACT_APP_POKAPI_URL,
    });

    const handler = (response: ErrorResponse) => {
      if (response.status === 403) {
        notifications.unauthorized();
      }
      throw response;
    };

    setDefaultContext(
      getContext(
        new APIWithHandler(
          configuration,
          handler,
          async () => {
            if (waitingRoomRef.current) {
              await waitingRoomRef.current();
            }
          },
          auth,
        ),
        () =>
          JSON.parse(sessionStorage.getItem('pok-company') || '{}')?.id || '',
        companyId,
        setCompanyId,
        () => JSON.parse(sessionStorage.getItem('pok-language') || '""') || '',
        language,
        setLanguage,
        ensureConnected => (waitingRoomRef.current = ensureConnected),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth,
    notifications,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    sessionStorage.getItem('pok-company'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    sessionStorage.getItem('pok-language'),
  ]);

  return (
    <PokCoreContext.Provider value={context}>
      {/* {auth.mutex ? children : <Waiting />} */}
      {children}
    </PokCoreContext.Provider>
  );
};
